/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import { AviPermissionResource, IALBServicesSiteInfo } from 'generated-types';
import { Item } from 'ajs/modules/data-model/factories/item.factory';

interface IAlbServiceSiteConfig extends IALBServicesSiteInfo {
    // Name property for IItemGenericConfig compatibility
    name?: string;
}

/**
 * Interface for Alb Service Site data structure from API.
 */
interface IAlbServiceSiteData {
    config: IAlbServiceSiteConfig;
}

/**
 * Ajs dependency token for AlbServicesSite.
 */
export const ALB_SERVICES_SITE_ITEM_TOKEN = 'AlbServicesSite';

/**
 * @description Alb Services Site item.
 * @author Deepak Chaudhary, Shubam Bindal
 */
export class AlbServicesSiteItem extends Item<IAlbServiceSiteConfig> {
    public getConfig!: () => IAlbServiceSiteConfig;
    public data!: IAlbServiceSiteData;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'albservices/site',
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_SYSTEMCONFIGURATION,
            ...args,
        };

        super(extendedArgs);
    }

    /** @override */
    public getName(): string {
        const { site_id: siteId, site_name: siteName } = this.getConfig() ?? {};

        if (siteId && siteName) {
            return `${siteId}:${siteName}`;
        } else if (siteId && !siteName) {
            return siteId;
        } else if (!siteId && siteName) {
            return siteName;
        }

        return '';
    }

    /** @override */
    public getRef(): string {
        return this.getConfig()?.site_id ?? '';
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    protected getIdFromData_(data: IAlbServiceSiteData): string {
        return data?.config?.site_id ?? '';
    }
}
